import React, { useMemo, useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Page from '../../components/layout/Page';
import Card, { CardHeader, CardTitle, CardDescription, CardContent } from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import { useAppConfig } from '../../lib/AppConfigContext';
import { useRole } from '../../lib/roles';
import { storage } from '../../firebase';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import Icon from '../../components/AppIcon';
import { useToast } from '../../lib/ToastContext';
import ControlHub from '../ControlHub';
import AppLogPanel from './components/AppLogPanel';
import StoreNetworkSettings from './components/StoreNetworkSettings';
import StoreLocationSettings from './components/StoreLocationSettings';

/**
 * Settings Page - Comprehensive configuration interface
 * Admin-only configuration workspace
 */

const SettingsPage = () => {
  const { config, updateConfig, loading } = useAppConfig();
  const { role } = useRole();
  const location = useLocation();
  const navigate = useNavigate();
  const isAdmin = role === 'admin';
  const toast = useToast();
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [isMobileSectionsOpen, setIsMobileSectionsOpen] = useState(false);
  const controlHubSectionByTab = useMemo(() => ({
    'admin-overview': 'overview',
    'admin-users': 'users',
    'admin-integrations': 'integrations',
    'admin-system': 'system',
  }), []);

  // Local state for form fields
  const [formData, setFormData] = useState({
    appName: config?.branding?.appName || 'GoldBuy Portal',
    primaryColor: config?.branding?.primaryColor || '#17191D',
    secondaryColor: config?.branding?.secondaryColor || '#656C78',
    accentColor: config?.branding?.accentColor || '#2457DB',
    animationsEnabled: config?.theme?.animationsEnabled ?? true,
  });

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleLogoUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file (PNG, JPG, SVG, etc.)');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File is too large. Maximum size is 5 MB.');
      return;
    }

    try {
      setUploadingLogo(true);
      const storageRef = ref(storage, `branding/logo_${Date.now()}.${file.name.split('.').pop()}`);
      await uploadBytes(storageRef, file);
      const logoUrl = await getDownloadURL(storageRef);

      await updateConfig({
        branding: {
          ...config.branding,
          logoUrl,
        },
      });

      toast.success('Logo uploaded successfully!');
    } catch (error) {
      console.error('Error uploading logo:', error);
      toast.error('Failed to upload logo. Please try again.');
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleSaveBranding = async () => {
    try {
      setSaving(true);
      await updateConfig({
        branding: {
          ...config.branding,
          appName: formData.appName,
          primaryColor: formData.primaryColor,
          secondaryColor: formData.secondaryColor,
          accentColor: formData.accentColor,
        },
      });
      toast.success('Branding settings saved!');
    } catch (error) {
      console.error('Error saving branding:', error);
      toast.error('Failed to save settings. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveAppearance = async () => {
    try {
      setSaving(true);
      await updateConfig({
        theme: {
          ...config.theme,
          animationsEnabled: formData.animationsEnabled,
        },
      });
      toast.success('Appearance settings saved!');
    } catch (error) {
      console.error('Error saving appearance:', error);
      toast.error('Failed to save settings. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const tabs = useMemo(() => {
    if (!isAdmin) return [];
    return [
      { id: 'app-log', label: 'App Log', icon: 'FileText' },
      { id: 'branding', label: 'Branding', icon: 'Palette' },
      { id: 'appearance', label: 'Appearance', icon: 'Sparkles' },
      { id: 'preferences', label: 'Preferences', icon: 'Settings' },
      { id: 'store-network', label: 'Store Network', icon: 'Wifi' },
      { id: 'store-location', label: 'Store Location', icon: 'MapPin' },
      { id: 'admin-overview', label: 'Admin Overview', icon: 'LayoutDashboard' },
      { id: 'admin-users', label: 'Users & Roles', icon: 'Users' },
      { id: 'admin-integrations', label: 'Integrations', icon: 'Plug' },
      { id: 'admin-system', label: 'System & Logs', icon: 'Server' },
    ];
  }, [isAdmin]);

  const defaultTab = useMemo(() => {
    const queryTab = new URLSearchParams(location.search).get('tab');
    const normalizedQuery = (
      queryTab === 'admin' ? 'admin-overview' :
      queryTab === 'overview' ? 'admin-overview' :
      queryTab === 'users' ? 'admin-users' :
      queryTab === 'integrations' ? 'admin-integrations' :
      queryTab === 'logs' ? 'app-log' :
      queryTab === 'system' ? 'admin-system' :
      queryTab
    );
    if (normalizedQuery && tabs.some(t => t.id === normalizedQuery)) return normalizedQuery;
    return tabs[0]?.id || 'branding';
  }, [location.search, tabs, isAdmin]);

  const [activeTab, setActiveTab] = useState(defaultTab);

  useEffect(() => {
    setActiveTab(defaultTab);
  }, [defaultTab]);

  const setTab = (tabId) => {
    setActiveTab(tabId);
    setIsMobileSectionsOpen(false);
    navigate(`/settings?tab=${tabId}`, { replace: true });
  };

  const activeControlHubSection = controlHubSectionByTab[activeTab] || null;
  const activeTabMeta = tabs.find((tab) => tab.id === activeTab);

  return (
    <Page
      header={(
        <div className="mb-8 animate-slide-up flex items-start justify-between gap-4">
          <div>
            <h1 className="text-3xl md:text-4xl font-semibold text-foreground mb-2">
              Settings
            </h1>
            <p className="text-muted-foreground text-base">
              Customize your portal experience
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            iconName="Menu"
            className="lg:hidden"
            onClick={() => setIsMobileSectionsOpen(true)}
          >
            {activeTabMeta?.label || 'Sections'}
          </Button>
        </div>
      )}
    >
      {isMobileSectionsOpen && (
        <div
          className="lg:hidden fixed inset-0 z-[70] bg-black/40 backdrop-blur-sm"
          onClick={(e) => {
            if (e.target === e.currentTarget) setIsMobileSectionsOpen(false);
          }}
        >
          <div className="absolute top-0 left-0 right-0 bg-card border-b border-border p-4 max-h-[85vh] overflow-y-auto shadow-elevation-2">
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm font-semibold text-foreground">Settings Sections</p>
              <button
                type="button"
                onClick={() => setIsMobileSectionsOpen(false)}
                className="p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors"
                aria-label="Close sections menu"
              >
                <Icon name="X" size={18} />
              </button>
            </div>
            <div className="space-y-1">
              {tabs.map((tab) => (
                <button
                  key={`mobile-${tab.id}`}
                  onClick={() => setTab(tab.id)}
                  className={`w-full text-left px-3 py-2.5 rounded-lg border transition-colors flex items-center gap-2 text-sm font-medium ${
                    activeTab === tab.id
                      ? 'bg-primary/10 text-foreground border-primary/30'
                      : 'bg-transparent text-muted-foreground border-transparent hover:text-foreground hover:bg-muted/40 hover:border-border'
                  }`}
                >
                  <Icon name={tab.icon} size={16} />
                  <span>{tab.label}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-[260px_minmax(0,1fr)] gap-6 items-start">
        <Card variant="solid" hover="none" padding="sm" className="hidden lg:block lg:sticky lg:top-20 animate-fade-in">
          <p className="px-2 pb-2 text-xs font-semibold tracking-wide text-muted-foreground uppercase">
            Sections
          </p>
          <div className="space-y-1">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setTab(tab.id)}
                className={`w-full text-left px-3 py-2.5 rounded-lg border transition-colors flex items-center gap-2 text-sm font-medium ${
                  activeTab === tab.id
                    ? 'bg-primary/10 text-foreground border-primary/30'
                    : 'bg-transparent text-muted-foreground border-transparent hover:text-foreground hover:bg-muted/40 hover:border-border'
                }`}
              >
                <Icon name={tab.icon} size={16} />
                <span>{tab.label}</span>
              </button>
            ))}
          </div>
        </Card>

        {/* Tab Content */}
        <div className="space-y-6">
        {activeTab === 'store-network' && isAdmin && <StoreNetworkSettings />}
        {activeTab === 'store-location' && isAdmin && <StoreLocationSettings />}
        {/* Embedded Admin Control sections */}
        {activeControlHubSection && isAdmin && (
          <div className="space-y-6 animate-fade-in">
            <ControlHub
              embedded
              hideHeader
              hideTabs
              activeSection={activeControlHubSection}
            />
          </div>
        )}

        {/* Branding Tab */}
        {activeTab === 'branding' && isAdmin && (
          <div className="space-y-6 animate-fade-in">
              <Card variant="solid">
                <CardHeader>
                  <CardTitle>Brand Identity</CardTitle>
                  <CardDescription>Customize your app's branding and visual identity</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-6 mt-6">
                    {/* Logo Upload */}
                    <div>
                      <label className="block text-sm font-semibold mb-3">Logo</label>
                      <div className="flex items-center gap-4 flex-wrap">
                        {config?.branding?.logoUrl && (
                          <div className="w-20 h-20 rounded-xl bg-card border border-border flex items-center justify-center overflow-hidden">
                            <img src={config.branding.logoUrl} alt="Logo" className="max-w-full max-h-full object-contain" />
                          </div>
                        )}
                        <div>
                          <input
                            type="file"
                            accept="image/*"
                            onChange={handleLogoUpload}
                            className="hidden"
                            id="logo-upload"
                          />
                          <label htmlFor="logo-upload">
                            <Button variant="outline" iconName="Upload" asChild loading={uploadingLogo} className="cursor-pointer">
                              {uploadingLogo ? 'Uploading...' : 'Upload Logo'}
                            </Button>
                          </label>
                        </div>
                      </div>
                    </div>

                    {/* App Name */}
                    <Input
                      label="App Name"
                      value={formData.appName}
                      onChange={(e) => handleInputChange('appName', e.target.value)}
                      placeholder="GoldBuy Portal"
                    />

                    {/* Color Pickers */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-semibold mb-2">Primary Color</label>
                        <div className="flex items-center gap-3">
                          <input
                            type="color"
                            value={formData.primaryColor}
                            onChange={(e) => handleInputChange('primaryColor', e.target.value)}
                            className="w-12 h-12 rounded-lg cursor-pointer border border-border"
                          />
                          <Input
                            value={formData.primaryColor}
                            onChange={(e) => handleInputChange('primaryColor', e.target.value)}
                            placeholder="#A888FF"
                            className="flex-1"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-semibold mb-2">Secondary Color</label>
                        <div className="flex items-center gap-3">
                          <input
                            type="color"
                            value={formData.secondaryColor}
                            onChange={(e) => handleInputChange('secondaryColor', e.target.value)}
                            className="w-12 h-12 rounded-lg cursor-pointer border border-border"
                          />
                          <Input
                            value={formData.secondaryColor}
                            onChange={(e) => handleInputChange('secondaryColor', e.target.value)}
                            placeholder="#00D4AA"
                            className="flex-1"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-semibold mb-2">Accent Color</label>
                        <div className="flex items-center gap-3">
                          <input
                            type="color"
                            value={formData.accentColor}
                            onChange={(e) => handleInputChange('accentColor', e.target.value)}
                            className="w-12 h-12 rounded-lg cursor-pointer border border-border"
                          />
                          <Input
                            value={formData.accentColor}
                            onChange={(e) => handleInputChange('accentColor', e.target.value)}
                            placeholder="#FFB800"
                            className="flex-1"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Live Preview */}
                    <div className="mt-6 p-6 rounded-xl bg-muted/40 border border-border">
                      <p className="text-sm font-semibold mb-3">Preview</p>
                      <div className="flex gap-3 flex-wrap">
                        <div
                          className="px-4 py-2 rounded-lg font-semibold"
                          style={{ backgroundColor: formData.primaryColor, color: '#FFFFFF' }}
                        >
                          Primary
                        </div>
                        <div
                          className="px-4 py-2 rounded-lg font-semibold"
                          style={{ backgroundColor: formData.secondaryColor, color: '#FFFFFF' }}
                        >
                          Secondary
                        </div>
                        <div
                          className="px-4 py-2 rounded-lg font-semibold"
                          style={{ backgroundColor: formData.accentColor, color: '#FFFFFF' }}
                        >
                          Accent
                        </div>
                      </div>
                    </div>

                    {/* Save Button */}
                    <div className="flex justify-end pt-4">
                      <Button
                        variant="default"
                        iconName="Save"
                        onClick={handleSaveBranding}
                        loading={saving}
                      >
                        Save Branding
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* App Log Tab */}
          {activeTab === 'app-log' && isAdmin && (
            <AppLogPanel />
          )}

          {/* Appearance Tab */}
          {activeTab === 'appearance' && (
            <div className="space-y-6 animate-fade-in">
              <Card variant="solid">
                <CardHeader>
                  <CardTitle>Visual Preferences</CardTitle>
                  <CardDescription>Adjust the visual appearance of the interface</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-6 mt-6">
                    {/* Animations Toggle */}
                    <div className="flex items-center justify-between p-4 rounded-xl bg-muted/40 border border-border">
                      <div>
                        <p className="font-semibold">Enable Animations</p>
                        <p className="text-sm text-muted-foreground">Smooth transitions and effects</p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.animationsEnabled}
                          onChange={(e) => handleInputChange('animationsEnabled', e.target.checked)}
                          className="sr-only peer"
                        />
                        <div className="w-14 h-7 bg-muted peer-focus:ring-4 peer-focus:ring-primary/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[4px] after:bg-white after:border-border after:border after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-primary"></div>
                      </label>
                    </div>

                    {/* Save Button */}
                    <div className="flex justify-end pt-4">
                      <Button
                        variant="default"
                        iconName="Save"
                        onClick={handleSaveAppearance}
                        loading={saving}
                      >
                        Save Appearance
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Preferences Tab */}
          {activeTab === 'preferences' && (
            <div className="space-y-6 animate-fade-in">
              <Card variant="solid">
                <CardHeader>
                  <CardTitle>User Preferences</CardTitle>
                  <CardDescription>Customize your personal settings</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="p-8 text-center opacity-60">
                    <Icon name="Settings" size={48} className="mx-auto text-muted-foreground/50 mb-4" />
                    <p className="text-sm font-medium text-muted-foreground">Coming Soon</p>
                    <p className="text-xs text-muted-foreground/70 mt-1">User preferences will be available in a future update.</p>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      </div>
    </Page>
  );
};

export default SettingsPage;
